import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { isValid, parseISO } from 'date-fns';

import { PinnedFilters } from 'components/common/PinnedFilters/PinnedFilters';
import { selectGenericTicketsPinnedFilters } from '@store';
import { useAssignees, useDateTimeRange } from '@hooks';
import { useTeams } from 'components/common/QuickTicketCreationForm/hooks';
import { toCreatedByOptions } from 'hooks/genericTickets/utils';
import { URLS } from 'layouts/DrawerLayout/constants';
import {
  GENERIC_TICKETS_STATUS_OPTIONS,
  GENERIC_TICKET_PRIORITY_OPTIONS,
  GENERIC_TICKET_REASON_OPTIONS,
} from '../constants';

const getFilterValue = (queryParam, data) =>
  queryParam
    ?.split(',')
    ?.map((item) =>
      data.find((option) => option.value.toString() === item.toString())
    ) || [];

const getDateTimeISOString = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const parsedValue = parseISO(value);
    return isValid(parsedValue) ? parsedValue.toISOString() : null;
  }

  if (value instanceof Date && isValid(value)) {
    return value.toISOString();
  }

  return null;
};

const GenericTicketsFilters = () => {
  const router = useRouter();
  const pinnedFilters = useSelector(selectGenericTicketsPinnedFilters);
  const { handleDateRangeChange } = useDateTimeRange();
  const { assigneesWithNone, assignees } = useAssignees();
  const { activeTeams: teams } = useTeams(false);

  const createdByOptions = useMemo(
    () => toCreatedByOptions(assignees),
    [assignees]
  );

  const handleChangeSingleFilter = (value, filterName) => {
    const filterValue = value?.value ?? value;
    if (
      filterValue !== null &&
      filterValue !== undefined &&
      filterValue !== ''
    ) {
      router.query[filterName] = filterValue;
    } else {
      delete router.query[filterName];
    }
    router.query.page = '1';
    router.push(router);
  };

  const handleChangeMultiFilter = (value, filterName) => {
    router.query.page = 1;
    if (value.length) {
      router.query[filterName] = value.map((item) => item.value).join(',');
    } else {
      delete router.query[filterName];
    }
    router.push(router);
  };

  const handleDateTimeChange = (value, filterName) => {
    const newQuery = { ...router.query };
    newQuery.page = '1';
    const nextValue = getDateTimeISOString(value);

    if (value && !nextValue) {
      return;
    }

    if (nextValue) {
      newQuery[filterName] = nextValue;
    } else {
      delete newQuery[filterName];
    }

    router.push({
      pathname: URLS.genericTickets,
      query: newQuery,
    });
  };

  const FILTERS_CONFIG = [
    {
      key: 'status',
      type: 'filterMultiselect',
      label: 'Status',
      width: '150px',
      minWidth: '150px',
      isMulti: true,
      options: GENERIC_TICKETS_STATUS_OPTIONS,
      value: getFilterValue(
        router.query?.status,
        GENERIC_TICKETS_STATUS_OPTIONS
      ),
    },
    {
      key: 'team',
      type: 'filterMultiselect',
      label: 'Team',
      width: '200px',
      minWidth: '200px',
      isMulti: true,
      options: teams,
      value: getFilterValue(router.query?.team, teams),
    },
    {
      key: 'assignee',
      type: 'filterAutocomplete',
      label: 'Assignee',
      width: '260px',
      minWidth: '260px',
      options: assigneesWithNone,
      value:
        assigneesWithNone.find((a) => a.value === router.query?.assignee) ||
        null,
    },
    {
      key: 'dueDate',
      type: 'filterDateTime',
      label: 'Due Date & time',
      width: '220px',
      minWidth: '260px',
      is24hr: true,
      value: router.query?.dueDate || null,
      onChange: (value) => handleDateTimeChange(value, 'dueDate'),
    },
    {
      key: 'priority',
      type: 'filterMultiselect',
      label: 'Priority',
      width: '150px',
      minWidth: '150px',
      isMulti: true,
      options: GENERIC_TICKET_PRIORITY_OPTIONS,
      value: getFilterValue(
        router.query?.priority,
        GENERIC_TICKET_PRIORITY_OPTIONS
      ),
    },
    {
      key: 'reason',
      type: 'filterMultiselect',
      label: 'Ticket Reason',
      width: '260px',
      minWidth: '260px',
      isMulti: true,
      options: GENERIC_TICKET_REASON_OPTIONS,
      value: getFilterValue(
        router.query?.reason,
        GENERIC_TICKET_REASON_OPTIONS
      ),
    },
    {
      key: 'createdBy',
      type: 'filterMultiselect',
      label: 'Created By',
      width: '260px',
      minWidth: '260px',
      isMulti: true,
      options: createdByOptions,
      value: getFilterValue(router.query?.createdBy, createdByOptions),
    },
    {
      key: 'createdAt',
      type: 'dateRangePicker',
      label: 'Created',
      startText: 'Created From',
      endText: 'Created To',
      width: '200px',
      minWidth: '260px',
      queryKeys: ['dateFrom', 'dateTo'],
      value: [router.query?.dateFrom || null, router.query?.dateTo || null],
      onChange: (value) =>
        handleDateRangeChange(value, URLS.genericTickets, true),
    },
  ];

  return (
    <PinnedFilters
      pinnedFilters={pinnedFilters}
      filtersConfig={FILTERS_CONFIG}
      handleChangeSingleFilter={handleChangeSingleFilter}
      handleChangeMultiFilter={handleChangeMultiFilter}
      useFilterItemContainer
    />
  );
};

export default GenericTicketsFilters;
